/**
 * @fileoverview NFE.io SDK v3 - Main Client
 *
 * @description
 * Core client class for interacting with the NFE.io API v1.
 * Provides a modern TypeScript interface with zero runtime dependencies.
 *
 * @module nfe-io/client
 * @author NFE.io
 * @license MIT
 */

import type {
  NfeConfig,
  RequiredNfeConfig,
  ServiceInvoice,
  PollOptions
} from './types.js';
import { HttpClient, createDefaultRetryConfig, buildHttpConfig } from './http/client.js';
import { ErrorFactory, ConfigurationError, PollingTimeoutError } from './errors/index.js';

// Resource imports
import {
  ServiceInvoicesResource,
  CompaniesResource,
  LegalPeopleResource,
  NaturalPeopleResource,
  WebhooksResource,
  AddressesResource,
  TransportationInvoicesResource,
  InboundProductInvoicesResource,
  ProductInvoiceQueryResource,
  ConsumerInvoiceQueryResource,
  LegalEntityLookupResource,
  NaturalPersonLookupResource,
  TaxCalculationResource,
  TaxCodesResource,
  ProductInvoicesResource,
  StateTaxesResource,
  ServiceInvoicesRtcResource,
  ProductInvoicesRtcResource,
  MunicipalTaxesResource,
  ConsumerInvoicesResource,
  CertificatesResource,
  NotificationsResource,
  ADDRESS_API_BASE_URL,
  NFE_QUERY_API_BASE_URL,
  LEGAL_ENTITY_API_BASE_URL,
  NATURAL_PERSON_API_BASE_URL
} from './resources/index.js';
import { VERSION as PKG_VERSION } from '../version.js';

// ============================================================================
// Constants
// ============================================================================

/** Base URL for CT-e API (Transportation Invoices) */
export const CTE_API_BASE_URL = 'https://api.nfse.io';

/** Base URL for Legal Entity API (CNPJ Lookup) */
export { LEGAL_ENTITY_API_BASE_URL } from './resources/index.js';

/** Base URL for Natural Person API (CPF Lookup) */
export { NATURAL_PERSON_API_BASE_URL } from './resources/index.js';

// ============================================================================
// Main NFE.io Client
// ============================================================================

/**
 * Main NFE.io API Client
 *
 * @description
 * Primary client class for interacting with the NFE.io API. Provides access to all
 * API resources including service invoices, companies, legal/natural people, and webhooks.
 *
 * **Features:**
 * - Zero runtime dependencies (uses native fetch)
 * - Automatic retry with exponential backoff
 * - TypeScript type safety
 * - Async invoice processing with polling utilities
 * - Environment detection and validation
 *
 * @example Basic Usage
 * ```typescript
 * import { NfeClient } from 'nfe-io';
 *
 * const nfe = new NfeClient({
 *   apiKey: 'your-api-key',
 *   environment: 'production' // or 'sandbox'
 * });
 *
 * // Create a company
 * const company = await nfe.companies.create({
 *   federalTaxNumber: '12345678000190',
 *   name: 'My Company'
 * });
 *
 * // Issue a service invoice
 * const invoice = await nfe.serviceInvoices.create(company.id, {
 *   borrower: { /* ... *\/ },
 *   cityServiceCode: '12345',
 *   servicesAmount: 1000.00
 * });
 * ```
 *
 * @example With Custom Configuration
 * ```typescript
 * const nfe = new NfeClient({
 *   apiKey: process.env.NFE_API_KEY,
 *   environment: 'production',
 *   timeout: 60000, // 60 seconds
 *   retryConfig: {
 *     maxRetries: 5,
 *     baseDelay: 1000,
 *     maxDelay: 30000
 *   }
 * });
 * ```
 *
 * @example Async Invoice Processing
 * ```typescript
 * // Method 1: Manual polling
 * const result = await nfe.serviceInvoices.create(companyId, data);
 * if (result.status === 'pending') {
 *   const invoice = await nfe.pollUntilComplete(
 *     () => nfe.serviceInvoices.retrieve(companyId, result.id)
 *   );
 * }
 *
 * // Method 2: Automatic polling (recommended)
 * const invoice = await nfe.serviceInvoices.createAndWait(companyId, data, {
 *   maxAttempts: 30,
 *   interval: 2000 // Check every 2 seconds
 * });
 * ```
 *
 * @see {@link NfeConfig} for configuration options
 * @see {@link ServiceInvoicesResource} for invoice operations
 * @see {@link CompaniesResource} for company operations
 */
export class NfeClient {
  /** @internal HTTP client for main API requests (created lazily) */
  private _http: HttpClient | undefined;

  /** @internal HTTP client for address API requests (created lazily) */
  private _addressHttp: HttpClient | undefined;

  /** @internal HTTP client for CT-e API requests (created lazily) */
  private _nfseHttp: HttpClient | undefined;
  private _webhooksAccountHttp: HttpClient | undefined;

  /** @internal HTTP client for NF-e query API requests (created lazily) */
  private _nfeQueryHttp: HttpClient | undefined;

  /** @internal HTTP client for Legal Entity API requests (created lazily) */
  private _legalEntityHttp: HttpClient | undefined;

  /** @internal HTTP client for Natural Person API requests (created lazily) */
  private _naturalPersonHttp: HttpClient | undefined;

  /** @internal Normalized client configuration */
  private readonly config: RequiredNfeConfig;

  /** @internal Cached resource instances */
  private _serviceInvoices: ServiceInvoicesResource | undefined;
  private _companies: CompaniesResource | undefined;
  private _legalPeople: LegalPeopleResource | undefined;
  private _naturalPeople: NaturalPeopleResource | undefined;
  private _webhooks: WebhooksResource | undefined;
  private _addresses: AddressesResource | undefined;
  private _transportationInvoices: TransportationInvoicesResource | undefined;
  private _inboundProductInvoices: InboundProductInvoicesResource | undefined;
  private _productInvoiceQuery: ProductInvoiceQueryResource | undefined;
  private _consumerInvoiceQuery: ConsumerInvoiceQueryResource | undefined;
  private _legalEntityLookup: LegalEntityLookupResource | undefined;
  private _naturalPersonLookup: NaturalPersonLookupResource | undefined;
  private _taxCalculation: TaxCalculationResource | undefined;
  private _taxCodes: TaxCodesResource | undefined;
  private _productInvoices: ProductInvoicesResource | undefined;
  private _stateTaxes: StateTaxesResource | undefined;
  private _serviceInvoicesRtc: ServiceInvoicesRtcResource | undefined;
  private _productInvoicesRtc: ProductInvoicesRtcResource | undefined;
  private _municipalTaxes: MunicipalTaxesResource | undefined;
  private _consumerInvoices: ConsumerInvoicesResource | undefined;
  private _certificates: CertificatesResource | undefined;
  private _notifications: NotificationsResource | undefined;

  /**
   * Service Invoices API resource
   *
   * @description
   * Provides operations for managing service invoices (NFS-e):
   * - Create, list, retrieve, cancel service invoices
   * - Send invoices by email
   * - Download PDF and XML files
   * - Automatic polling for async invoice processing
   *
   * @see {@link ServiceInvoicesResource}
   * @throws {ConfigurationError} If API key is not configured
   *
   * @example
   * ```typescript
   * const invoice = await nfe.serviceInvoices.create(companyId, {
   *   borrower: { name: 'Client', email: 'client@example.com' },
   *   cityServiceCode: '12345',
   *   servicesAmount: 1000.00
   * });
   * ```
   */
  get serviceInvoices(): ServiceInvoicesResource {
    if (!this._serviceInvoices) {
      this._serviceInvoices = new ServiceInvoicesResource(this.getMainHttpClient());
    }
    return this._serviceInvoices;
  }

  /**
   * Companies API resource
   *
   * @description
   * Provides operations for managing companies:
   * - CRUD operations for companies
   * - Upload digital certificates (PFX/P12)
   * - Batch operations
   *
   * @see {@link CompaniesResource}
   * @throws {ConfigurationError} If API key is not configured
   *
   * @example
   * ```typescript
   * const company = await nfe.companies.create({
   *   federalTaxNumber: '12345678000190',
   *   name: 'My Company',
   *   email: 'company@example.com'
   * });
   * ```
   */
  get companies(): CompaniesResource {
    if (!this._companies) {
      this._companies = new CompaniesResource(this.getMainHttpClient(), this.getNfseHttpClient());
    }
    return this._companies;
  }

  /**
   * Legal People API resource
   *
   * @description
   * Provides operations for managing legal persons (empresas/PJ):
   * - CRUD operations scoped by company
   * - CNPJ lookup and validation
   * - Batch operations
   *
   * @see {@link LegalPeopleResource}
   * @throws {ConfigurationError} If API key is not configured
   *
   * @example
   * ```typescript
   * const legalPerson = await nfe.legalPeople.create(companyId, {
   *   federalTaxNumber: '12345678000190',
   *   name: 'Legal Person Company'
   * });
   * ```
   */
  get legalPeople(): LegalPeopleResource {
    if (!this._legalPeople) {
      this._legalPeople = new LegalPeopleResource(this.getMainHttpClient());
    }
    return this._legalPeople;
  }

  /**
   * Natural People API resource
   *
   * @description
   * Provides operations for managing natural persons (pessoas físicas/PF):
   * - CRUD operations scoped by company
   * - CPF lookup and validation
   * - Batch operations
   *
   * @see {@link NaturalPeopleResource}
   * @throws {ConfigurationError} If API key is not configured
   *
   * @example
   * ```typescript
   * const naturalPerson = await nfe.naturalPeople.create(companyId, {
   *   federalTaxNumber: '12345678901',
   *   name: 'John Doe'
   * });
   * ```
   */
  get naturalPeople(): NaturalPeopleResource {
    if (!this._naturalPeople) {
      this._naturalPeople = new NaturalPeopleResource(this.getMainHttpClient());
    }
    return this._naturalPeople;
  }

  /**
   * Webhooks API resource
   *
   * @description
   * Provides operations for managing webhooks (account-scoped, `/v2/webhooks`):
   * - CRUD operations for webhook configurations (`*AccountWebhook*` methods)
   * - Webhook signature validation
   * - Ping/test webhook delivery
   * - Fetch available event types from the live API
   *
   * The company-scoped methods (`create`, `list`, ...) are deprecated — the
   * `/v1/companies/{id}/webhooks` route returns 404 on the current API.
   *
   * @see {@link WebhooksResource}
   * @throws {ConfigurationError} If API key is not configured
   *
   * @example
   * ```typescript
   * const webhook = await nfe.webhooks.createAccountWebhook({
   *   uri: 'https://example.com/webhook', // precisa responder 2xx já na criação
   *   contentType: 'json',
   *   secret: 'um-segredo-de-32-a-64-caracteres-aqui',
   *   filters: ['service_invoice.issued_successfully', 'service_invoice.cancelled_successfully'],
   * });
   * ```
   */
  get webhooks(): WebhooksResource {
    if (!this._webhooks) {
      this._webhooks = new WebhooksResource(
        this.getMainHttpClient(),
        this.getWebhooksAccountHttpClient()
      );
    }
    return this._webhooks;
  }

  /**
   * Addresses API resource
   *
   * @description
   * Provides postal code (CEP) lookup for Brazilian addresses. The live API host
   * supports postal-code lookup only and returns a single {@link Address}.
   *
   * **Note:** This resource uses a different API host (address.api.nfe.io).
   * Configure `dataApiKey` for a separate key, or it will fallback to `apiKey`.
   *
   * @see {@link AddressesResource}
   * @throws {ConfigurationError} If no API key is configured (dataApiKey or apiKey)
   *
   * @example
   * ```typescript
   * const address = await nfe.addresses.lookupByPostalCode('01310-100');
   * console.log(address.street); // 'Paulista'
   * ```
   */
  get addresses(): AddressesResource {
    if (!this._addresses) {
      this._addresses = new AddressesResource(this.getAddressHttpClient());
    }
    return this._addresses;
  }

  /**
   * Transportation Invoices (CT-e) API resource
   *
   * @description
   * Provides operations for managing CT-e (Conhecimento de Transporte Eletrônico)
   * documents via SEFAZ Distribuição DFe:
   * - Enable/disable automatic CT-e search
   * - Retrieve CT-e metadata and XML
   * - Retrieve CT-e event metadata and XML
   *
   * **Prerequisites:**
   * - Company must have a valid A1 digital certificate
   * - Webhook must be configured to receive CT-e notifications
   *
   * **Note:** This resource uses a different API host (api.nfse.io).
   * Uses the main `apiKey` — `api.nfse.io` is a FISCAL host and rejects the data key with 403.
   *
   * @see {@link TransportationInvoicesResource}
   * @throws {ConfigurationError} If no main API key is configured (apiKey)
   *
   * @example
   * ```typescript
   * // Enable automatic CT-e search
   * await nfe.transportationInvoices.enable('company-id');
   *
   * // Retrieve CT-e metadata
   * const cte = await nfe.transportationInvoices.retrieve(
   *   'company-id',
   *   '35240112345678000190570010000001231234567890'
   * );
   * ```
   */
  get transportationInvoices(): TransportationInvoicesResource {
    if (!this._transportationInvoices) {
      this._transportationInvoices = new TransportationInvoicesResource(this.getNfseHttpClient());
    }
    return this._transportationInvoices;
  }

  /**
   * Inbound Product Invoices (NF-e Distribution) API resource
   *
   * @description
   * Provides operations for querying NF-e documents received by a company
   * via SEFAZ Distribuição DFe:
   * - Enable/disable automatic NF-e distribution fetch
   * - Retrieve inbound NF-e metadata by access key
   * - Download NF-e documents in XML, PDF, and JSON formats
   * - Send recipient manifest (Manifestação do Destinatário)
   * - Reprocess webhooks
   *
   * **Prerequisites:**
   * - Company must have a valid A1 digital certificate
   * - Webhook must be configured to receive NF-e notifications
   *
   * **Note:** This resource uses a different API host (api.nfse.io).
   * Uses the main `apiKey` — `api.nfse.io` is a FISCAL host and rejects the data key with 403.
   *
   * @see {@link InboundProductInvoicesResource}
   * @throws {ConfigurationError} If no main API key is configured (apiKey)
   *
   * @example
   * ```typescript
   * // Enable automatic NF-e fetch
   * await nfe.inboundProductInvoices.enableAutoFetch('company-id', {
   *   startFromNsu: '999999',
   *   environmentSEFAZ: 'Production',
   *   webhookVersion: '2'
   * });
   *
   * // Get NF-e details
   * const doc = await nfe.inboundProductInvoices.getProductInvoiceDetails(
   *   'company-id',
   *   '35240112345678000190550010000001231234567890'
   * );
   * ```
   */
  get inboundProductInvoices(): InboundProductInvoicesResource {
    if (!this._inboundProductInvoices) {
      this._inboundProductInvoices = new InboundProductInvoicesResource(this.getNfseHttpClient());
    }
    return this._inboundProductInvoices;
  }

  /**
   * Product Invoice Query (NF-e) API resource
   *
   * @description
   * Provides read-only operations for querying product invoices (NF-e) directly
   * on SEFAZ by access key — no company scope required:
   * - Retrieve full invoice details (issuer, buyer, items, totals, transport, payment)
   * - Download DANFE PDF
   * - Download raw NF-e XML
   * - List fiscal events (cancellations, corrections, manifestations)
   *
   * **Note:** This resource uses a different API host (nfe.api.nfe.io).
   * Configure `dataApiKey` for a separate key, or it will fallback to `apiKey`.
   *
   * @see {@link ProductInvoiceQueryResource}
   * @throws {ConfigurationError} If no API key is configured (dataApiKey or apiKey)
   *
   * @example
   * ```typescript
   * // Retrieve invoice details
   * const invoice = await nfe.productInvoiceQuery.retrieve(
   *   '35240112345678000190550010000001231234567890'
   * );
   * console.log(invoice.currentStatus); // 'authorized'
   * console.log(invoice.issuer?.name);
   *
   * // Download PDF
   * const pdf = await nfe.productInvoiceQuery.downloadPdf(
   *   '35240112345678000190550010000001231234567890'
   * );
   * fs.writeFileSync('danfe.pdf', pdf);
   *
   * // List fiscal events
   * const events = await nfe.productInvoiceQuery.listEvents(
   *   '35240112345678000190550010000001231234567890'
   * );
   * ```
   */
  get productInvoiceQuery(): ProductInvoiceQueryResource {
    if (!this._productInvoiceQuery) {
      this._productInvoiceQuery = new ProductInvoiceQueryResource(this.getNfeQueryHttpClient());
    }
    return this._productInvoiceQuery;
  }

  /**
   * Consumer Invoice Query API resource (CFe-SAT)
   *
   * @description
   * Provides read-only operations for querying CFe-SAT (Cupom Fiscal Eletrônico)
   * consumer invoices by access key. No company scope required.
   *
   * - Retrieve full coupon details (issuer, buyer, items, totals, payment)
   * - Download original CFe XML
   *
   * Uses data API key authentication on `nfe.api.nfe.io`.
   *
   * @see {@link ConsumerInvoiceQueryResource}
   * @throws {ConfigurationError} If API key is not configured
   *
   * @example
   * ```typescript
   * // Retrieve coupon details
   * const coupon = await nfe.consumerInvoiceQuery.retrieve(
   *   '35240112345678000190590000000012341234567890'
   * );
   * console.log(coupon.currentStatus); // 'Authorized'
   * console.log(coupon.issuer?.name);
   *
   * // Download CFe XML
   * const xml = await nfe.consumerInvoiceQuery.downloadXml(
   *   '35240112345678000190590000000012341234567890'
   * );
   * fs.writeFileSync('cfe.xml', xml);
   * ```
   */
  get consumerInvoiceQuery(): ConsumerInvoiceQueryResource {
    if (!this._consumerInvoiceQuery) {
      this._consumerInvoiceQuery = new ConsumerInvoiceQueryResource(this.getNfeQueryHttpClient());
    }
    return this._consumerInvoiceQuery;
  }

  /**
   * Legal Entity Lookup API resource (CNPJ)
   *
   * @description
   * Provides read-only operations for querying Brazilian company (CNPJ) data:
   * - Basic company info (Receita Federal registry data)
   * - State tax registration (Inscrição Estadual) lookup
   * - State tax evaluation for invoice issuance
   * - Suggested optimal IE for invoice issuance
   *
   * **Note:** This resource uses a different API host (legalentity.api.nfe.io).
   * Configure `dataApiKey` for a separate key, or it will fallback to `apiKey`.
   *
   * @see {@link LegalEntityLookupResource}
   * @throws {ConfigurationError} If no API key is configured (dataApiKey or apiKey)
   *
   * @example
   * ```typescript
   * // Basic CNPJ lookup
   * const result = await nfe.legalEntityLookup.getBasicInfo('12.345.678/0001-90');
   * console.log(result.legalEntity?.name);
   *
   * // State tax registration
   * const stateTax = await nfe.legalEntityLookup.getStateTaxInfo('SP', '12345678000190');
   * ```
   */
  get legalEntityLookup(): LegalEntityLookupResource {
    if (!this._legalEntityLookup) {
      this._legalEntityLookup = new LegalEntityLookupResource(this.getLegalEntityHttpClient());
    }
    return this._legalEntityLookup;
  }

  /**
   * Natural Person Lookup API resource (CPF)
   *
   * @description
   * Provides a read-only operation for querying CPF cadastral status (situação cadastral)
   * at the Brazilian Federal Revenue Service (Receita Federal).
   *
   * **Note:** This resource uses a different API host (naturalperson.api.nfe.io).
   * Configure `dataApiKey` for a separate key, or it will fallback to `apiKey`.
   *
   * @see {@link NaturalPersonLookupResource}
   * @throws {ConfigurationError} If no API key is configured (dataApiKey or apiKey)
   *
   * @example
   * ```typescript
   * // CPF cadastral status lookup
   * const result = await nfe.naturalPersonLookup.getStatus('123.456.789-01', '1990-01-15');
   * console.log(result.name);    // 'JOÃO DA SILVA'
   * console.log(result.status);  // 'Regular'
   * ```
   */
  get naturalPersonLookup(): NaturalPersonLookupResource {
    if (!this._naturalPersonLookup) {
      this._naturalPersonLookup = new NaturalPersonLookupResource(this.getNaturalPersonHttpClient());
    }
    return this._naturalPersonLookup;
  }

  /**
   * Tax Calculation Engine API resource
   *
   * @description
   * Provides access to the Motor de Cálculo de Tributos (Tax Calculation Engine)
   * for computing all applicable Brazilian taxes (ICMS, ICMS-ST, PIS, COFINS,
   * IPI, II) on product operations.
   *
   * **Note:** This resource uses a different API host (api.nfse.io).
   * Uses the main `apiKey` — `api.nfse.io` is a FISCAL host and rejects the data key with 403.
   *
   * @see {@link TaxCalculationResource}
   * @throws {ConfigurationError} If no main API key is configured (apiKey)
   *
   * @example
   * ```typescript
   * const result = await nfe.taxCalculation.calculate('tenant-id', {
   *   operationType: 'Outgoing',
   *   issuer: { state: 'SP', taxRegime: 'RealProfit' },
   *   recipient: { state: 'RJ' },
   *   items: [{
   *     id: '1', operationCode: 121, origin: 'National',
   *     quantity: 10, unitAmount: 100.00, ncm: '61091000'
   *   }]
   * });
   * ```
   */
  get taxCalculation(): TaxCalculationResource {
    if (!this._taxCalculation) {
      this._taxCalculation = new TaxCalculationResource(this.getNfseHttpClient());
    }
    return this._taxCalculation;
  }

  /**
   * Tax Codes API resource (auxiliary reference tables)
   *
   * @description
   * Provides paginated listings of auxiliary tax code reference tables
   * needed as inputs for the Tax Calculation Engine: operation codes,
   * acquisition purposes, issuer tax profiles, and recipient tax profiles.
   *
   * **Note:** This resource uses a different API host (api.nfse.io).
   * Uses the main `apiKey` — `api.nfse.io` is a FISCAL host and rejects the data key with 403.
   *
   * @see {@link TaxCodesResource}
   * @see {@link TaxCalculationResource}
   * @throws {ConfigurationError} If no main API key is configured (apiKey)
   *
   * @example
   * ```typescript
   * const codes = await nfe.taxCodes.listOperationCodes({ pageIndex: 1, pageCount: 20 });
   * for (const code of codes.items ?? []) {
   *   console.log(`${code.code} - ${code.description}`);
   * }
   * ```
   */
  get taxCodes(): TaxCodesResource {
    if (!this._taxCodes) {
      this._taxCodes = new TaxCodesResource(this.getNfseHttpClient());
    }
    return this._taxCodes;
  }

  /**
   * Product Invoices (NF-e) API resource
   *
   * @description
   * Provides full lifecycle management for NF-e (Nota Fiscal Eletrônica de Produto)
   * product invoices — issue, list, retrieve, cancel, send correction letters (CC-e),
   * disable invoice numbers, and download files (PDF/XML).
   *
   * **Note:** This resource uses the api.nfse.io host.
   * Uses the main `apiKey` — `api.nfse.io` is a FISCAL host and rejects the data key with 403.
   *
   * @see {@link ProductInvoicesResource}
   * @throws {ConfigurationError} If no main API key is configured (apiKey)
   *
   * @example
   * ```typescript
   * const result = await nfe.productInvoices.create('company-id', invoiceData);
   * const invoices = await nfe.productInvoices.list('company-id', { environment: 'Production' });
   * ```
   */
  get productInvoices(): ProductInvoicesResource {
    if (!this._productInvoices) {
      this._productInvoices = new ProductInvoicesResource(this.getNfseHttpClient());
    }
    return this._productInvoices;
  }

  /**
   * State Taxes (Inscrições Estaduais) API resource
   *
   * @description
   * Provides CRUD operations for company state tax registrations required for
   * NF-e product invoice issuance — list, create, retrieve, update, and delete.
   *
   * **Note:** This resource uses the api.nfse.io host.
   * Uses the main `apiKey` — `api.nfse.io` is a FISCAL host and rejects the data key with 403.
   *
   * @see {@link StateTaxesResource}
   * @throws {ConfigurationError} If no main API key is configured (apiKey)
   *
   * @example
   * ```typescript
   * const taxes = await nfe.stateTaxes.list('company-id');
   * const tax = await nfe.stateTaxes.create('company-id', { taxNumber: '123', serie: 1, number: 1 });
   * ```
   */
  get stateTaxes(): StateTaxesResource {
    if (!this._stateTaxes) {
      this._stateTaxes = new StateTaxesResource(this.getNfseHttpClient());
    }
    return this._stateTaxes;
  }

  /**
   * Service Invoices RTC resource — emit NFS-e under the Reforma Tributária layout
   * (IBS/CBS groups). Uses the main host (api.nfe.io); supports polling. Emission
   * is opt-in via this resource; retrieve/cancel/PDF/XML are shared with
   * {@link serviceInvoices}.
   */
  get serviceInvoicesRtc(): ServiceInvoicesRtcResource {
    if (!this._serviceInvoicesRtc) {
      this._serviceInvoicesRtc = new ServiceInvoicesRtcResource(this.getMainHttpClient());
    }
    return this._serviceInvoicesRtc;
  }

  /**
   * Product Invoices RTC resource — emit NF-e/NFC-e under the Reforma Tributária
   * layout (IBS state+municipal, CBS, IS). Uses api.nfse.io; webhook-driven (not
   * polled), mirroring {@link productInvoices}.
   */
  get productInvoicesRtc(): ProductInvoicesRtcResource {
    if (!this._productInvoicesRtc) {
      this._productInvoicesRtc = new ProductInvoicesRtcResource(this.getNfseHttpClient());
    }
    return this._productInvoicesRtc;
  }

  /**
   * Municipal Taxes resource — CRUD for company municipal tax registrations
   * (Inscrições Municipais), prerequisite for NFS-e issuance. Uses api.nfse.io.
   */
  get municipalTaxes(): MunicipalTaxesResource {
    if (!this._municipalTaxes) {
      this._municipalTaxes = new MunicipalTaxesResource(this.getNfseHttpClient());
    }
    return this._municipalTaxes;
  }

  /**
   * Consumer Invoices resource — emit & manage NFC-e (company-scoped) on
   * api.nfse.io. Webhook-driven emission. Distinct from {@link consumerInvoiceQuery}
   * (read-only coupon lookup).
   */
  get consumerInvoices(): ConsumerInvoicesResource {
    if (!this._consumerInvoices) {
      this._consumerInvoices = new ConsumerInvoicesResource(this.getNfseHttpClient());
    }
    return this._consumerInvoices;
  }

  /**
   * Certificates resource — manage company digital certificates (retrieve/delete
   * by thumbprint, list) via the contribuintes-v2 API on api.nfse.io. Complements
   * the legacy `companies.uploadCertificate` (which targets the api.nfe.io host).
   */
  get certificates(): CertificatesResource {
    if (!this._certificates) {
      this._certificates = new CertificatesResource(this.getNfseHttpClient());
    }
    return this._certificates;
  }

  /**
   * Notifications resource — company notification operations (api.nfe.io).
   */
  get notifications(): NotificationsResource {
    if (!this._notifications) {
      this._notifications = new NotificationsResource(this.getMainHttpClient());
    }
    return this._notifications;
  }

  /**
   * Create a new NFE.io API client
   *
   * @param config - Client configuration options
   * @throws {ConfigurationError} If configuration is invalid
   * @throws {ConfigurationError} If Node.js version < 18
   * @throws {ConfigurationError} If fetch API is not available
   *
   * @example Basic
   * ```typescript
   * const nfe = new NfeClient({
   *   apiKey: 'your-api-key',
   *   environment: 'production'
   * });
   * ```
   *
   * @example With environment variable
   * ```typescript
   * // Set NFE_API_KEY environment variable
   * const nfe = new NfeClient({
   *   environment: 'production'
   * });
   * ```
   *
   * @example With custom retry config
   * ```typescript
   * const nfe = new NfeClient({
   *   apiKey: 'your-api-key',
   *   timeout: 60000,
   *   retryConfig: {
   *     maxRetries: 5,
   *     baseDelay: 1000,
   *     maxDelay: 30000
   *   }
   * });
   * ```
   *
   * @example With only data API key
   * ```typescript
   * // Only use data services (address lookup, CT-e), no main API access
   * const nfe = new NfeClient({
   *   dataApiKey: 'data-api-key'
   * });
   * await nfe.addresses.lookupByPostalCode('01310-100');
   * ```
   */
  constructor(config: NfeConfig = {}) {
    // Validate Node.js environment first
    this.validateEnvironment();

    // Validate and normalize configuration (no longer requires apiKey)
    this.config = this.validateAndNormalizeConfig(config);

    // Resources are initialized lazily via getters
  }

  // --------------------------------------------------------------------------
  // HTTP Client Management (Lazy Initialization)
  // --------------------------------------------------------------------------

  /**
   * Get or create the main API HTTP client
   * @throws {ConfigurationError} If no API key is configured
   */
  private getMainHttpClient(): HttpClient {
    if (!this._http) {
      const apiKey = this.resolveMainApiKey();
      if (!apiKey) {
        throw new ConfigurationError(
          'API key required for this resource. Set "apiKey" in config or NFE_API_KEY environment variable.'
        );
      }
      const httpConfig = buildHttpConfig(
        apiKey,
        this.config.baseUrl,
        this.config.timeout,
        this.config.retryConfig
      );
      this._http = new HttpClient(httpConfig);
    }
    return this._http;
  }

  /**
   * Get or create the Address API HTTP client
   * @throws {ConfigurationError} If no API key is configured
   */
  private getAddressHttpClient(): HttpClient {
    if (!this._addressHttp) {
      const apiKey = this.resolveDataApiKey();
      if (!apiKey) {
        throw new ConfigurationError(
          'API key required for data services. Set "dataApiKey" or "apiKey" in config, or NFE_DATA_API_KEY/NFE_API_KEY environment variable.'
        );
      }
      const httpConfig = buildHttpConfig(
        apiKey,
        ADDRESS_API_BASE_URL,
        this.config.timeout,
        this.config.retryConfig
      );
      this._addressHttp = new HttpClient(httpConfig);
    }
    return this._addressHttp;
  }

  /**
   * Resolve the main API key using fallback chain
   */
  private resolveMainApiKey(): string | undefined {
    return (
      this.config.apiKey ||
      this.getEnvironmentVariable('NFE_API_KEY')
    );
  }

  /**
   * Resolve the data API key using fallback chain
   * Order: dataApiKey → apiKey → NFE_DATA_API_KEY → NFE_API_KEY
   */
  private resolveDataApiKey(): string | undefined {
    return (
      this.config.dataApiKey ||
      this.config.apiKey ||
      this.getEnvironmentVariable('NFE_DATA_API_KEY') ||
      this.getEnvironmentVariable('NFE_API_KEY')
    );
  }

  /**
   * Get or create the HTTP client for `api.nfse.io` — the FISCAL host.
   *
   * Every resource on this host uses the MAIN api key. The two platform keys are
   * complementary, not interchangeable: the data key is rejected with 403 here,
   * and the main key is rejected with 403 on the lookup hosts
   * (`nfe.api.nfe.io`, `legalentity`, `naturalperson`, `address`).
   * Verified live on 2026-09-01 — see tests/fixtures/live-contracts/api-key-host-matrix.json.
   *
   * @throws {ConfigurationError} If no main API key is configured
   */
  private getNfseHttpClient(): HttpClient {
    if (!this._nfseHttp) {
      const apiKey = this.resolveMainApiKey();
      if (!apiKey) {
        throw new ConfigurationError(
          'API key required for this resource. Set "apiKey" in config or NFE_API_KEY environment variable.'
        );
      }
      const httpConfig = buildHttpConfig(
        apiKey,
        CTE_API_BASE_URL,
        this.config.timeout,
        this.config.retryConfig
      );
      this._nfseHttp = new HttpClient(httpConfig);
    }
    return this._nfseHttp;
  }

  /**
   * Get or create the HTTP client for ACCOUNT-scoped webhook endpoints. These
   * live at the host root under `/v2` (e.g. https://api.nfe.io/v2/webhooks) and
   * use the MAIN key — NOT under the `/v1` base of the main client.
   * @throws {ConfigurationError} If no main API key is configured
   */
  private getWebhooksAccountHttpClient(): HttpClient {
    if (!this._webhooksAccountHttp) {
      const apiKey = this.resolveMainApiKey();
      if (!apiKey) {
        throw new ConfigurationError(
          'API key required for this resource. Set "apiKey" in config or NFE_API_KEY environment variable.'
        );
      }
      // Account webhooks are at host-root /v2, not under the /v1 main base.
      const v2BaseUrl = this.config.baseUrl.replace(/\/v1(\/)?$/, '/v2');
      const httpConfig = buildHttpConfig(
        apiKey,
        v2BaseUrl,
        this.config.timeout,
        this.config.retryConfig
      );
      this._webhooksAccountHttp = new HttpClient(httpConfig);
    }
    return this._webhooksAccountHttp;
  }

  /**
   * Get or create the NF-e Query API HTTP client (nfe.api.nfe.io)
   * @throws {ConfigurationError} If no API key is configured
   */
  private getNfeQueryHttpClient(): HttpClient {
    if (!this._nfeQueryHttp) {
      const apiKey = this.resolveDataApiKey();
      if (!apiKey) {
        throw new ConfigurationError(
          'API key required for data services. Set "dataApiKey" or "apiKey" in config, or NFE_DATA_API_KEY/NFE_API_KEY environment variable.'
        );
      }
      const httpConfig = buildHttpConfig(
        apiKey,
        NFE_QUERY_API_BASE_URL,
        this.config.timeout,
        this.config.retryConfig
      );
      this._nfeQueryHttp = new HttpClient(httpConfig);
    }
    return this._nfeQueryHttp;
  }

  /**
   * Get or create the Legal Entity API HTTP client (legalentity.api.nfe.io)
   * @throws {ConfigurationError} If no API key is configured
   */
  private getLegalEntityHttpClient(): HttpClient {
    if (!this._legalEntityHttp) {
      const apiKey = this.resolveDataApiKey();
      if (!apiKey) {
        throw new ConfigurationError(
          'API key required for data services. Set "dataApiKey" or "apiKey" in config, or NFE_DATA_API_KEY/NFE_API_KEY environment variable.'
        );
      }
      const httpConfig = buildHttpConfig(
        apiKey,
        LEGAL_ENTITY_API_BASE_URL,
        this.config.timeout,
        this.config.retryConfig
      );
      this._legalEntityHttp = new HttpClient(httpConfig);
    }
    return this._legalEntityHttp;
  }

  /**
   * Get or create the Natural Person API HTTP client (naturalperson.api.nfe.io)
   * @throws {ConfigurationError} If no API key is configured
   */
  private getNaturalPersonHttpClient(): HttpClient {
    if (!this._naturalPersonHttp) {
      const apiKey = this.resolveDataApiKey();
      if (!apiKey) {
        throw new ConfigurationError(
          'API key required for data services. Set "dataApiKey" or "apiKey" in config, or NFE_DATA_API_KEY/NFE_API_KEY environment variable.'
        );
      }
      const httpConfig = buildHttpConfig(
        apiKey,
        NATURAL_PERSON_API_BASE_URL,
        this.config.timeout,
        this.config.retryConfig
      );
      this._naturalPersonHttp = new HttpClient(httpConfig);
    }
    return this._naturalPersonHttp;
  }

  // --------------------------------------------------------------------------
  // Configuration Management
  // --------------------------------------------------------------------------

  private validateAndNormalizeConfig(config: NfeConfig): RequiredNfeConfig {
    // API keys are now optional - validated lazily when resources are accessed
    const apiKey = config.apiKey?.trim() || undefined;
    const dataApiKey = config.dataApiKey?.trim() || undefined;

    // Normalize environment
    const environment = config.environment || 'production';
    if (!['production', 'development'].includes(environment)) {
      throw new ConfigurationError(
        `Invalid environment: ${environment}. Must be 'production' or 'development'.`,
        { environment }
      );
    }

    // Set defaults - ensure retryConfig has all required properties
    const defaultRetryConfig = createDefaultRetryConfig();
    const retryConfig = config.retryConfig
      ? { ...defaultRetryConfig, ...config.retryConfig }
      : defaultRetryConfig;

    const normalizedConfig: RequiredNfeConfig = {
      apiKey,
      dataApiKey,
      environment,
      baseUrl: config.baseUrl || this.getDefaultBaseUrl(),
      timeout: config.timeout || 30000,
      retryConfig,
    };

    return normalizedConfig;
  }

  private getDefaultBaseUrl(): string {
    // NFE.io API uses the same endpoint for both production and development
    // They are differentiated by the API key used, not by different URLs
    return 'https://api.nfe.io/v1';
  }

  private getEnvironmentVariable(name: string): string | undefined {
    // Safe access to process.env with fallback
    try {
      return (globalThis as any).process?.env?.[name];
    } catch {
      return undefined;
    }
  }

  // --------------------------------------------------------------------------
  // Environment Validation
  // --------------------------------------------------------------------------

  private validateEnvironment(): void {
    // Check Node.js version (should support fetch natively)
    this.validateNodeVersion();

    // Check fetch availability
    if (typeof fetch === 'undefined') {
      throw ErrorFactory.fromNodeVersionError(this.getNodeVersion());
    }
  }

  private validateNodeVersion(): void {
    const nodeVersion = this.getNodeVersion();
    const majorVersion = this.extractMajorVersion(nodeVersion);

    if (majorVersion < 18) {
      throw ErrorFactory.fromNodeVersionError(nodeVersion);
    }
  }

  private getNodeVersion(): string {
    try {
      return (globalThis as any).process?.version || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  private extractMajorVersion(version: string): number {
    const match = version.match(/^v?(\d+)\./);
    return match ? parseInt(match[1]!, 10) : 0;
  }

  // --------------------------------------------------------------------------
  // Public Utility Methods
  // --------------------------------------------------------------------------

  /**
   * Update client configuration dynamically
   *
   * @param newConfig - Partial configuration to merge with existing config
   * @throws {ConfigurationError} If new configuration is invalid
   *
   * @example
   * ```typescript
   * const nfe = new NfeClient({ apiKey: 'old-key' });
   *
   * // Switch to sandbox environment
   * nfe.updateConfig({ environment: 'sandbox' });
   *
   * // Update timeout
   * nfe.updateConfig({ timeout: 60000 });
   * ```
   */
  public updateConfig(newConfig: Partial<NfeConfig>): void {
    // Normalize the new configuration with current values as defaults
    const normalizedConfig = this.validateAndNormalizeConfig({
      ...newConfig,
      // Use current values as fallbacks for unspecified fields
      environment: newConfig.environment ?? this.config.environment,
      baseUrl: newConfig.baseUrl ?? this.config.baseUrl,
      timeout: newConfig.timeout ?? this.config.timeout,
      retryConfig: newConfig.retryConfig ?? this.config.retryConfig,
    });

    // Override API keys if they were in current config but not in newConfig
    if (normalizedConfig.apiKey === undefined && this.config.apiKey !== undefined && newConfig.apiKey === undefined) {
      normalizedConfig.apiKey = this.config.apiKey;
    }
    if (normalizedConfig.dataApiKey === undefined && this.config.dataApiKey !== undefined && newConfig.dataApiKey === undefined) {
      normalizedConfig.dataApiKey = this.config.dataApiKey;
    }

    // Update internal config
    Object.assign(this.config, normalizedConfig);

    // Clear ALL cached HTTP clients and resources so they're recreated with new config.
    this.resetCaches();
  }

  /**
   * Invalidate every lazily-cached HTTP client and resource.
   *
   * Must list every `_*Http` and resource field so that, after `updateConfig`,
   * no cached instance retains a stale baseUrl/apiKey/timeout. When adding a new
   * resource or HTTP client, add it here too (single source of cache truth).
   */
  private resetCaches(): void {
    // HTTP clients
    this._http = undefined;
    this._addressHttp = undefined;
    this._nfseHttp = undefined;
    this._webhooksAccountHttp = undefined;
    this._nfeQueryHttp = undefined;
    this._legalEntityHttp = undefined;
    this._naturalPersonHttp = undefined;
    // Resources
    this._serviceInvoices = undefined;
    this._companies = undefined;
    this._legalPeople = undefined;
    this._naturalPeople = undefined;
    this._webhooks = undefined;
    this._addresses = undefined;
    this._transportationInvoices = undefined;
    this._inboundProductInvoices = undefined;
    this._productInvoiceQuery = undefined;
    this._consumerInvoiceQuery = undefined;
    this._legalEntityLookup = undefined;
    this._naturalPersonLookup = undefined;
    this._taxCalculation = undefined;
    this._taxCodes = undefined;
    this._productInvoices = undefined;
    this._stateTaxes = undefined;
    this._serviceInvoicesRtc = undefined;
    this._productInvoicesRtc = undefined;
    this._municipalTaxes = undefined;
    this._consumerInvoices = undefined;
    this._certificates = undefined;
    this._notifications = undefined;
  }

  /**
   * Set request timeout in milliseconds
   *
   * @param timeout - Request timeout in milliseconds
   *
   * @description
   * Maintains v2 API compatibility. Equivalent to `updateConfig({ timeout })`.
   *
   * @example
   * ```typescript
   * nfe.setTimeout(60000); // 60 seconds
   * ```
   */
  public setTimeout(timeout: number): void {
    this.updateConfig({ timeout });
  }

  /**
   * Set or update API key
   *
   * @param apiKey - New API key to use for authentication
   *
   * @description
   * Maintains v2 API compatibility. Equivalent to `updateConfig({ apiKey })`.
   *
   * @example
   * ```typescript
   * nfe.setApiKey('new-api-key');
   * ```
   */
  public setApiKey(apiKey: string): void {
    this.updateConfig({ apiKey });
  }

  /**
   * Get current client configuration
   *
   * @returns Readonly copy of current configuration
   *
   * @example
   * ```typescript
   * const config = nfe.getConfig();
   * console.log('Environment:', config.environment);
   * console.log('Base URL:', config.baseUrl);
   * console.log('Timeout:', config.timeout);
   * ```
   */
  public getConfig(): Readonly<RequiredNfeConfig> {
    return { ...this.config };
  }

  // --------------------------------------------------------------------------
  // Polling Utility (for async invoice processing)
  // --------------------------------------------------------------------------

  /**
   * Poll a resource until it completes or times out
   *
   * @template T - Type of the resource being polled
   * @param locationUrl - URL or path to poll
   * @param options - Polling configuration
   * @returns Promise that resolves when resource is complete
   * @throws {PollingTimeoutError} If polling exceeds maxAttempts
   *
   * @description
   * Critical utility for NFE.io's async invoice processing. When creating a service
   * invoice, the API returns a 202 response with a location URL. This method polls
   * that URL until the invoice is fully processed or the polling times out.
   *
   * @example Basic usage
   * ```typescript
   * const result = await nfe.serviceInvoices.create(companyId, data);
   *
   * if (result.status === 'pending') {
   *   const invoice = await nfe.pollUntilComplete(result.location);
   *   console.log('Invoice issued:', invoice.number);
   * }
   * ```
   *
   * @example With custom polling options
   * ```typescript
   * const invoice = await nfe.pollUntilComplete(locationUrl, {
   *   maxAttempts: 60,  // Poll up to 60 times
   *   intervalMs: 3000  // Wait 3 seconds between attempts
   * });
   * ```
   *
   * @example Using createAndWait (recommended)
   * ```typescript
   * // Instead of manual polling, use the convenience method:
   * const invoice = await nfe.serviceInvoices.createAndWait(companyId, data, {
   *   maxAttempts: 30,
   *   interval: 2000
   * });
   * ```
   *
   * @see {@link PollOptions} for configuration options
   * @see {@link ServiceInvoicesResource.createAndWait} for automated polling
   */
  public async pollUntilComplete<T = ServiceInvoice>(
    locationUrl: string,
    options: PollOptions = {}
  ): Promise<T> {
    const {
      maxAttempts = 30,
      intervalMs = 2000
    } = options;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Wait before polling (except first attempt)
      if (attempt > 0) {
        await this.sleep(intervalMs);
      }

      try {
        // Extract path from full URL for HTTP client
        const path = this.extractPathFromUrl(locationUrl);
        const response = await this.getMainHttpClient().get<any>(path);

        // Check completion status
        if (this.isCompleteResponse(response.data)) {
          return response.data as T;
        }

        if (this.isFailedResponse(response.data)) {
          throw new PollingTimeoutError(
            `Resource processing failed: ${response.data.error || 'Unknown error'}`,
            response.data
          );
        }

        // Continue polling if still in progress

      } catch (error) {
        // If it's the last attempt, throw the error
        if (attempt === maxAttempts - 1) {
          throw error;
        }

        // For other attempts, continue polling (might be temporary network issue)
      }
    }

    throw new PollingTimeoutError(
      `Polling timeout after ${maxAttempts} attempts. Resource may still be processing.`,
      { maxAttempts, intervalMs }
    );
  }

  private extractPathFromUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname + urlObj.search;
    } catch {
      // If URL parsing fails, assume it's already a path
      return url.startsWith('/') ? url : `/${url}`;
    }
  }

  private isCompleteResponse(data: any): boolean {
    return data && (
      data.status === 'completed' ||
      data.status === 'issued' ||
      (data.id && data.number && !data.status) // NFE.io completed invoices might not have explicit status
    );
  }

  private isFailedResponse(data: any): boolean {
    return data && (
      data.status === 'failed' ||
      data.status === 'error' ||
      data.error
    );
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // --------------------------------------------------------------------------
  // Health Check & Debug
  // --------------------------------------------------------------------------

  /**
   * Check if the client is properly configured and can reach the NFE.io API
   *
   * @returns Health check result with status and optional error details
   *
   * @description
   * Performs a simple API request to verify connectivity and authentication.
   * Useful for debugging connection issues or validating client configuration.
   *
   * Issues `GET /v1/companies` with no query string — the smallest request the
   * API accepts on this route. See the implementation note before you add a
   * pagination parameter here.
   *
   * @example
   * ```typescript
   * const health = await nfe.healthCheck();
   *
   * if (health.status === 'ok') {
   *   console.log('API connection successful!');
   * } else {
   *   console.error('API connection failed:', health.details);
   * }
   * ```
   *
   * @example In application startup
   * ```typescript
   * async function initializeApp() {
   *   const nfe = new NfeClient({ apiKey: process.env.NFE_API_KEY });
   *
   *   const health = await nfe.healthCheck();
   *   if (health.status !== 'ok') {
   *     throw new Error(`NFE.io API is not reachable: ${health.details?.error}`);
   *   }
   *
   *   console.log('NFE.io SDK initialized successfully');
   * }
   * ```
   */
  public async healthCheck(): Promise<{ status: 'ok' | 'error', details?: any }> {
    try {
      // Requisicao minima que a API aceita: `GET /v1/companies` sem query.
      //
      // NAO reintroduzir `pageCount`. `pageCount=1` responde
      // 400 "pageCount must be between 1 and 50" -- o limite inferior do servidor
      // esta um a mais do que a propria mensagem diz (medido em 2026-09-02).
      // Enquanto isso nao for corrigido upstream, qualquer valor aqui seria um
      // numero magico contornando defeito alheio; a rota sem query responde 200.
      await this.getMainHttpClient().get('/companies');
      return { status: 'ok' };
    } catch (error) {
      return {
        status: 'error',
        details: {
          error: error instanceof Error ? error.message : 'Unknown error',
          config: {
            baseUrl: this.config.baseUrl,
            environment: this.config.environment,
            hasApiKey: !!this.config.apiKey,
          }
        }
      };
    }
  }

  /**
   * Get client information for debugging and diagnostics
   *
   * @returns Client diagnostic information
   *
   * @description
   * Returns comprehensive information about the current SDK instance,
   * useful for bug reports and troubleshooting.
   *
   * @example
   * ```typescript
   * const info = nfe.getClientInfo();
   * console.log('SDK Version:', info.version);
   * console.log('Node Version:', info.nodeVersion);
   * console.log('Environment:', info.environment);
   * console.log('Base URL:', info.baseUrl);
   * ```
   *
   * @example In error reporting
   * ```typescript
   * try {
   *   await nfe.serviceInvoices.create(companyId, data);
   * } catch (error) {
   *   const info = nfe.getClientInfo();
   *   console.error('Error context:', {
   *     error: error.message,
   *     sdkInfo: info
   *   });
   * }
   * ```
   */
  public getClientInfo(): {
    version: string;
    nodeVersion: string;
    environment: string;
    baseUrl: string;
    hasApiKey: boolean;
  } {
    return {
      version: VERSION,
      nodeVersion: this.getNodeVersion(),
      environment: this.config.environment,
      baseUrl: this.config.baseUrl,
      hasApiKey: !!this.config.apiKey,
    };
  }
}

// ============================================================================
// Factory Functions (maintain v2 compatibility)
// ============================================================================

/**
 * Create NFE.io client instance using factory function
 *
 * @param apiKey - API key string or full configuration object
 * @param _version - API version (ignored in v3, maintained for v2 compatibility)
 * @returns Configured NfeClient instance
 *
 * @description
 * Factory function for creating NFE.io client instances. Maintains v2 API compatibility
 * while providing modern TypeScript support.
 *
 * @example String API key
 * ```typescript
 * const nfe = createNfeClient('your-api-key');
 * ```
 *
 * @example Configuration object
 * ```typescript
 * const nfe = createNfeClient({
 *   apiKey: 'your-api-key',
 *   environment: 'sandbox',
 *   timeout: 60000
 * });
 * ```
 *
 * @example v2 compatibility
 * ```typescript
 * // v2 style (still works)
 * const nfe = createNfeClient('your-api-key');
 * ```
 */
export function createNfeClient(apiKey: string | NfeConfig): NfeClient {
  const config = typeof apiKey === 'string' ? { apiKey } : apiKey;
  return new NfeClient(config);
}

/**
 * Default export factory function for CommonJS compatibility
 *
 * @param apiKey - API key string or full configuration object
 * @returns Configured NfeClient instance
 *
 * @description
 * Default export maintains v2 API compatibility for CommonJS users.
 * Equivalent to `createNfeClient()`.
 *
 * @example ES Modules
 * ```typescript
 * import nfe from 'nfe-io';
 * const client = nfe('your-api-key');
 * ```
 *
 * @example CommonJS
 * ```javascript
 * const nfe = require('nfe-io').default;
 * const client = nfe('your-api-key');
 * ```
 */
export default function nfe(apiKey: string | NfeConfig): NfeClient {
  return createNfeClient(apiKey);
}

// ============================================================================
// Version Constants
// ============================================================================

/**
 * Current SDK version
 *
 * Vem de `src/version.ts`, gerado do `package.json` — ver a nota em
 * `PACKAGE_VERSION` (`src/index.ts`) sobre por que não se fixa literal aqui.
 *
 * @constant
 */
export const VERSION = PKG_VERSION;

/**
 * Supported Node.js version range (semver format)
 * @constant
 */
export const SUPPORTED_NODE_VERSIONS = '>=18.0.0';

/**
 * Default request timeout in milliseconds
 * @constant
 */
export const DEFAULT_TIMEOUT = 30000;

/**
 * Default number of retry attempts for failed requests
 * @constant
 */
export const DEFAULT_RETRY_ATTEMPTS = 3;
